import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Zap, Clock, Shield, Users, Award, TrendingUp, Globe, HeartHandshake, CheckCircle2, Star } from 'lucide-react';
import teamPhoto from '../assets/serveses/ChatGPT Image Aug 20, 2026, 12_52_30 PM.png';

const whyUs = [
  {
    icon: <Zap size={20} />,
    title: 'Agile Delivery',
    desc: 'Sprint-based development with weekly deliverables ensures rapid, transparent project progress.',
    color: '#1A3BCC',
    light: '#EEF2FF',
  },
  {
    icon: <Shield size={20} />,
    title: 'Quality First',
    desc: 'Rigorous QA processes, code reviews, and automated testing baked into every stage.',
    color: '#059669',
    light: '#ECFDF5',
  },
  {
    icon: <Users size={20} />,
    title: 'Dedicated Teams',
    desc: 'You get a named team — not a rotating pool. Continuity, context, and commitment.',
    color: '#FF7B00',
    light: '#FFF7ED',
  },
  {
    icon: <Globe size={20} />,
    title: 'Full-Spectrum Tech',
    desc: 'AI, mobile, web, cloud, IoT — one partner for every technology need you have.',
    color: '#0EA5E9',
    light: '#F0F9FF',
  },
  {
    icon: <Clock size={20} />,
    title: 'On-Time Delivery',
    desc: 'We commit to deadlines and honour them — with transparent tracking dashboards.',
    color: '#7C3AED',
    light: '#F5F3FF',
  },
  {
    icon: <TrendingUp size={20} />,
    title: 'Scalable Solutions',
    desc: 'Architectures designed to scale — from 100 users to 10 million, without re-writes.',
    color: '#DC2626',
    light: '#FEF2F2',
  },
  {
    icon: <Award size={20} />,
    title: 'Certified Expertise',
    desc: 'Our team holds certifications in AWS, GCP, Android, and leading AI/ML frameworks.',
    color: '#D97706',
    light: '#FFFBEB',
  },
  {
    icon: <HeartHandshake size={20} />,
    title: 'Long-term Partnership',
    desc: 'We think beyond the project — post-launch support, maintenance, and evolution.',
    color: '#BE185D',
    light: '#FDF2F8',
  },
];

const stats = [
  { value: '50+', label: 'Projects Delivered' },
  { value: '30+', label: 'Expert Engineers' },
  { value: '98%', label: 'Client Satisfaction' },
  { value: '5+', label: 'Years of Impact' },
];

function FeatureCard({ item, index }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <motion.div
      ref={ref}
      initial={{ y: 30, opacity: 0 }}
      animate={inView ? { y: 0, opacity: 1 } : {}}
      transition={{ duration: 0.5, delay: index * 0.07, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="group flex gap-3 p-5 bg-white rounded-2xl border border-slate-100 shadow-card hover:shadow-royal transition-all duration-300 hover:-translate-y-1 hover:border-slate-200"
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300"
        style={{ background: item.light, color: item.color }}
      >
        {item.icon}
      </div>
      <div>
        <h4 className="font-syne font-bold text-slate-900 text-sm mb-1">{item.title}</h4>
        <p className="font-inter text-slate-500 text-xs leading-relaxed">{item.desc}</p>
      </div>
    </motion.div>
  );
}

export default function WhyUs() {
  const [titleRef, titleInView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Background blobs */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-brand-blue/3 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-brand-orange/4 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6">

        {/* Section Header */}
        <motion.div
          ref={titleRef}
          initial={{ y: 30, opacity: 0 }}
          animate={titleInView ? { y: 0, opacity: 1 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <span className="section-label mb-4 inline-block">Why Choose Us</span>
          <h2 className="font-syne font-extrabold text-4xl md:text-5xl text-slate-900 mb-4">
            The <span className="gradient-text">QuBright</span> Difference
          </h2>
          <p className="font-inter text-slate-500 text-lg max-w-2xl mx-auto leading-relaxed">
            We don't just write code — we architect solutions, build partnerships,
            and illuminate paths to digital excellence.
          </p>
        </motion.div>

        {/* Main Split Layout */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">

          {/* LEFT — Team Photo with overlays */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="relative"
          >
            {/* Gradient border frame */}
            <div
              className="rounded-3xl p-[3px] shadow-2xl"
              style={{
                background: 'linear-gradient(135deg, #1A3BCC 0%, #0EA5E9 55%, #FF7B00 100%)',
              }}
            >
              <div className="rounded-[22px] overflow-hidden relative">
                <img
                  src={teamPhoto}
                  alt="QuBright Technologies Team — Technology, Innovation, Impact"
                  className="w-full h-[420px] object-cover object-center transition-transform duration-700 hover:scale-105"
                />
                {/* Dark gradient overlay at bottom */}
                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-900/70 to-transparent" />

                {/* Bottom label */}
                <div className="absolute bottom-5 left-6 right-6 flex items-center justify-between">
                  <div>
                    <p className="text-white font-syne font-bold text-lg leading-tight">Our Expert Team</p>
                    <p className="text-blue-200 font-space text-xs tracking-widest uppercase mt-0.5">
                      Technology · Innovation · Impact
                    </p>
                  </div>
                  <div className="flex items-center gap-1 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1.5 border border-white/20">
                    <Star size={12} className="text-yellow-400 fill-yellow-400" />
                    <span className="text-white font-space font-bold text-xs">4.9</span>
                  </div>
                </div>

              </div>
            </div>

            {/* Stats grid below image */}
            <div className="grid grid-cols-4 gap-3 mt-5">
              {stats.map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0.8, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + i * 0.1, type: 'spring', stiffness: 200 }}
                  className="glass-card rounded-2xl p-3 text-center border border-brand-blue/10 hover:border-brand-blue/25 transition-all duration-300"
                >
                  <p className="font-syne font-extrabold text-brand-blue text-xl leading-none">{stat.value}</p>
                  <p className="font-space text-slate-500 text-[10px] mt-1 leading-tight">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* RIGHT — Feature Cards Grid */}
          <div>
            {/* Short intro */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-6"
            >
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 size={18} className="text-brand-blue" />
                <span className="font-space text-sm font-semibold text-brand-blue tracking-wide">
                  8 Reasons to Choose QuBright
                </span>
              </div>
              <p className="font-inter text-slate-500 text-sm leading-relaxed max-w-md">
                From rapid delivery to long-term partnerships — here's what sets our team apart from the rest.
              </p>
            </motion.div>

            {/* 2-col card grid */}
            <div className="grid sm:grid-cols-2 gap-4">
              {whyUs.map((item, i) => (
                <FeatureCard key={i} item={item} index={i} />
              ))}
            </div>
          </div>
        </div>

        {/* Bottom CTA Banner */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="relative overflow-hidden rounded-3xl"
          style={{
            background: 'linear-gradient(135deg, #1A3BCC 0%, #2563EB 50%, #0EA5E9 100%)',
          }}
        >
          {/* Subtle glow blobs */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-72 h-72 rounded-full bg-white blur-3xl" />
            <div className="absolute bottom-0 right-0 w-56 h-56 rounded-full bg-white blur-3xl" />
          </div>

          {/* Background team photo accent — right side */}
          <div className="absolute right-0 top-0 bottom-0 w-1/3 overflow-hidden opacity-15 hidden md:block">
            <img
              src={teamPhoto}
              alt=""
              aria-hidden="true"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-transparent to-transparent" />
          </div>

          <div className="relative z-10 p-10 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
            <div>
              <h3 className="font-syne font-extrabold text-3xl text-white mb-2">
                Ready to Build Something Brilliant?
              </h3>
              <p className="font-inter text-blue-200 text-base max-w-lg">
                Let's discuss your vision and craft a technology roadmap that propels your business forward.
              </p>
            </div>
            <div className="flex gap-3 shrink-0">
              <a href="#contact">
                <button className="bg-white text-brand-blue font-space font-bold px-8 py-4 rounded-xl hover:bg-blue-50 transition-colors duration-200 shadow-lg whitespace-nowrap">
                  Start a Project
                </button>
              </a>
              <a href="#services">
                <button className="border-2 border-white/40 text-white font-space font-semibold px-6 py-4 rounded-xl hover:bg-white/10 transition-colors duration-200 whitespace-nowrap">
                  View Services
                </button>
              </a>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
}
